import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TableToolbar } from "@/components/TableToolbar";
import { Badge, Card } from "@/components/ui";
import { documentKeys, reasonListQuery, type AdjustmentReason } from "@/features/documents/api";
import { ImportPanel } from "@/features/import/ImportPanel";
import { type ExportColumn } from "@/lib/exportXlsx";
import { useCan } from "@/lib/session";
import { useTableView, type TableColumn } from "@/components/TableView";

// Warehousing → Adjustment reasons: the tenant's reason dictionary for stock
// adjustments (in / out / both). A few dozen rows: one request, a plain
// table; maintained by import (ADR-034), seeded for every new organization.

interface Search {
  import?: "adjustment_reasons" | undefined;
}

export const Route = createFileRoute("/_app/warehousing/adjustment-reasons")({
  validateSearch: (raw: Record<string, unknown>): Search => (raw.import === "adjustment_reasons" ? { import: "adjustment_reasons" } : {}),
  loader: ({ context }) => context.queryClient.ensureQueryData(reasonListQuery),
  component: ReasonsPage,
});

const directionTone = { in: "success", out: "warning", both: "info" } as const;

function ReasonsPage() {
  const { t } = useTranslation();
  const reasonList = useQuery(reasonListQuery);
  const allReasons = useMemo(() => reasonList.data ?? [], [reasonList.data]);
  // A small dictionary loaded whole: column filters and sort in the browser.
  const reasonColumns = useMemo<TableColumn<AdjustmentReason>[]>(
    () => [
      { key: "code", header: t("reasons.colCode"), type: "text", value: (r) => r.code },
      { key: "description", header: t("reasons.colDescription"), type: "text", value: (r) => r.description },
      {
        key: "direction",
        header: t("reasons.colDirection"),
        type: "enum",
        value: (r) => r.direction,
        options: (["in", "out", "both"] as const).map((d) => ({ value: d, label: t(`reasons.direction.${d}`) })),
      },
      { key: "active", header: t("reasons.colStatus"), type: "bool", value: (r) => r.is_active },
    ],
    [t],
  );
  const table = useTableView(allReasons, reasonColumns);
  const reasons = table.rows;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const mayImport = useCan("core.data.import");
  const mayPost = useCan("inventory.adjustment.post");
  const canImport = mayImport && mayPost;

  const exportColumns: ExportColumn<AdjustmentReason>[] = [
    { header: "code", kind: "code", value: (r) => r.code },
    { header: "description", kind: "text", value: (r) => r.description },
    { header: "direction", kind: "code", value: (r) => r.direction },
    { header: "is_active", kind: "boolean", value: (r) => r.is_active },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.warehousing.title")} <span aria-hidden="true">/</span> {t("nav.warehousing.adjustmentReasons")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("reasons.title")}</h1>
        </div>
      </header>

      <p className="flex items-center gap-2 self-start rounded-lg bg-surface-muted px-3 py-1.5 text-[13px] text-ink-muted">
        <Info size={14} aria-hidden="true" />
        {t("reasons.note")}
      </p>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card aria-label={t("reasons.title")} className="self-start overflow-hidden">
          <TableToolbar
            label={t("reasons.title")}
            tools={{
              id: "adjustment-reasons",
              title: t("reasons.title"),
              onImport: canImport ? () => void navigate({ search: { import: "adjustment_reasons" }, replace: true }) : undefined,
            }}
            exportColumns={exportColumns}
            rows={reasons}
            onRefresh={() => void reasonList.refetch()}
            refreshing={reasonList.isRefetching}
            hasView={table.hasView}
            onClearView={table.clear}
          />
          {table.chips && <div className="border-b border-border-subtle px-3 py-2">{table.chips}</div>}
          {table.popover}
          <div className="overflow-x-auto">
            <table className="tabular w-full border-collapse text-[13px]" aria-label={t("reasons.title")}>
              <thead>
                <tr className="bg-surface-muted text-left text-ink-muted">
                  {table.th("code")}
                  {table.th("description")}
                  {table.th("direction")}
                  {table.th("active")}
                </tr>
              </thead>
              <tbody>
                {reasons.map((r) => (
                  <tr key={r.code} className="border-t border-border-subtle" style={{ height: "var(--row-h)" }}>
                    <td className="px-4 font-mono text-xs font-medium">{r.code}</td>
                    <td className="px-4">{r.description}</td>
                    <td className="px-4">
                      <Badge tone={directionTone[r.direction]}>{t(`reasons.direction.${r.direction}`)}</Badge>
                    </td>
                    <td className="px-4">
                      {r.is_active ? <Badge tone="success">{t("items.active")}</Badge> : <Badge tone="neutral">{t("items.inactive")}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        {search.import && (
          <Card aria-label={t("import.title")} className="flex min-h-0 flex-col self-start">
            <ImportPanel
              entities={["adjustment_reasons"]}
              initial="adjustment_reasons"
              onClose={() => void navigate({ search: {}, replace: true })}
              invalidate={[documentKeys.reasons()]}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
